import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Slider,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  LinearProgress,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Tune as TuneIcon,
  PlayArrow as RunIcon,
  Restore as RestoreIcon,
  Save as SaveIcon,
  Info as InfoIcon,
  TrendingUp as TrendIcon,
  ExpandMore as ExpandIcon,
  Speed as SpeedIcon,
  GpsFixed as TargetIcon,
  Balance as BalanceIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';

// Types for rule tuning
interface TunableRule {
  ruleId: string;
  ruleName: string;
  category: 'spectral' | 'structural' | 'geochemical' | 'geological' | 'temporal';
  description: string;
  currentThreshold: number;
  minThreshold: number;
  maxThreshold: number;
  step: number;
  unit?: string;
  operator: '>' | '<' | '>=' | '<=' | '==';
  enabled: boolean;
  weight: number;
  currentPerformance: {
    precision: number;
    recall: number;
    f1Score: number;
    truePositives: number;
    falsePositives: number;
    trueNegatives: number;
    falseNegatives: number;
  };
}

interface OptimizationPreset {
  id: string;
  name: string;
  description: string;
  target: 'precision' | 'recall' | 'f1_score' | 'balanced';
  rules: {
    ruleId: string;
    threshold: number;
    weight: number;
    enabled: boolean;
  }[];
}

interface PerformanceHistory {
  timestamp: string;
  overallF1: number;
  overallPrecision: number;
  overallRecall: number;
  changedRule?: string;
  changedFrom?: number;
  changedTo?: number;
}

interface InteractiveRuleTuningPanelProps {
  rules?: TunableRule[];
  onRuleChange?: (ruleId: string, threshold: number, weight: number, enabled: boolean) => void;
  onRunAnalysis?: (rules: TunableRule[]) => void;
  onSavePreset?: (preset: OptimizationPreset) => void;
  onLoadPreset?: (presetId: string) => void;
  isAnalyzing?: boolean;
}

// Default rules for demonstration
const DEFAULT_TUNABLE_RULES: TunableRule[] = [
  {
    ruleId: 'sericite_alt',
    ruleName: 'Sericite Alteration',
    category: 'spectral',
    description: 'SWIR1/SWIR2 ratio threshold for phyllic alteration detection',
    currentThreshold: 1.15,
    minThreshold: 1.0,
    maxThreshold: 1.5,
    step: 0.01,
    operator: '>',
    enabled: true,
    weight: 25,
    currentPerformance: {
      precision: 0.72,
      recall: 0.68,
      f1Score: 0.70,
      truePositives: 32,
      falsePositives: 12,
      trueNegatives: 445,
      falseNegatives: 15
    }
  },
  {
    ruleId: 'fault_prox',
    ruleName: 'Fault Proximity',
    category: 'structural', 
    description: 'Maximum distance from major fault structures',
    currentThreshold: 800,
    minThreshold: 200,
    maxThreshold: 2000,
    step: 50,
    unit: 'm',
    operator: '<',
    enabled: true,
    weight: 20,
    currentPerformance: {
      precision: 0.65,
      recall: 0.74,
      f1Score: 0.69,
      truePositives: 38,
      falsePositives: 20,
      trueNegatives: 430,
      falseNegatives: 14
    }
  },
  {
    ruleId: 'au_anomaly',
    ruleName: 'Gold Geochemical Anomaly',
    category: 'geochemical',
    description: 'Minimum Au concentration in stream sediments',
    currentThreshold: 15,
    minThreshold: 5,
    maxThreshold: 50,
    step: 1,
    unit: 'ppb',
    operator: '>',
    enabled: true,
    weight: 30,
    currentPerformance: {
      precision: 0.81,
      recall: 0.76,
      f1Score: 0.78,
      truePositives: 24,
      falsePositives: 6,
      trueNegatives: 465,
      falseNegatives: 7
    }
  },
  {
    ruleId: 'iron_oxide',
    ruleName: 'Iron Oxide Alteration',
    category: 'spectral',
    description: 'Red/Blue band ratio for iron oxide detection',
    currentThreshold: 2.0,
    minThreshold: 1.5,
    maxThreshold: 3.5,
    step: 0.1,
    operator: '>',
    enabled: true,
    weight: 15,
    currentPerformance: {
      precision: 0.58,
      recall: 0.62,
      f1Score: 0.60,
      truePositives: 29,
      falsePositives: 21,
      trueNegatives: 429,
      falseNegatives: 18
    }
  },
  {
    ruleId: 'host_rock',
    ruleName: 'Favorable Host Rock',
    category: 'geological',
    description: 'Host rock favorability score threshold',
    currentThreshold: 0.6,
    minThreshold: 0.3,
    maxThreshold: 0.9,
    step: 0.05,
    operator: '>',
    enabled: true,
    weight: 10,
    currentPerformance: {
      precision: 0.58,
      recall: 0.54,
      f1Score: 0.56,
      truePositives: 25,
      falsePositives: 18,
      trueNegatives: 432,
      falseNegatives: 21
    }
  }
];

// Optimization presets
const OPTIMIZATION_PRESETS: OptimizationPreset[] = [
  {
    id: 'high_precision',
    name: 'High Precision',
    description: 'Minimize false positives for targeted exploration',
    target: 'precision',
    rules: [
      { ruleId: 'sericite_alt', threshold: 1.25, weight: 30, enabled: true },
      { ruleId: 'fault_prox', threshold: 600, weight: 25, enabled: true },
      { ruleId: 'au_anomaly', threshold: 25, weight: 35, enabled: true },
      { ruleId: 'iron_oxide', threshold: 2.5, weight: 10, enabled: true },
      { ruleId: 'host_rock', threshold: 0.75, weight: 15, enabled: true }
    ]
  },
  {
    id: 'high_recall',
    name: 'High Recall',
    description: 'Minimize missed opportunities for comprehensive coverage',
    target: 'recall',
    rules: [
      { ruleId: 'sericite_alt', threshold: 1.05, weight: 20, enabled: true },
      { ruleId: 'fault_prox', threshold: 1200, weight: 20, enabled: true },
      { ruleId: 'au_anomaly', threshold: 8, weight: 25, enabled: true },
      { ruleId: 'iron_oxide', threshold: 1.7, weight: 20, enabled: true },
      { ruleId: 'host_rock', threshold: 0.45, weight: 15, enabled: true }
    ]
  },
  {
    id: 'balanced',
    name: 'Balanced F1',
    description: 'Optimal balance between precision and recall',
    target: 'f1_score',
    rules: [
      { ruleId: 'sericite_alt', threshold: 1.15, weight: 25, enabled: true },
      { ruleId: 'fault_prox', threshold: 800, weight: 20, enabled: true },
      { ruleId: 'au_anomaly', threshold: 15, weight: 30, enabled: true },
      { ruleId: 'iron_oxide', threshold: 2.0, weight: 15, enabled: true },
      { ruleId: 'host_rock', threshold: 0.6, weight: 10, enabled: true }
    ]
  }
];

const InteractiveRuleTuningPanel: React.FC<InteractiveRuleTuningPanelProps> = ({
  rules: propRules = [],
  onRuleChange,
  onRunAnalysis,
  onSavePreset,
  onLoadPreset,
  isAnalyzing = false
}) => {
  const [rules, setRules] = useState<TunableRule[]>(propRules.length > 0 ? propRules : DEFAULT_TUNABLE_RULES);
  const [selectedPreset, setSelectedPreset] = useState<string>('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [performanceHistory, setPerformanceHistory] = useState<PerformanceHistory[]>([]);
  const [autoOptimize, setAutoOptimize] = useState(false);
  const [optimizationTarget, setOptimizationTarget] = useState<'precision' | 'recall' | 'f1_score'>('f1_score');

  // Calculate overall performance metrics
  const overallPerformance = useMemo(() => {
    const enabledRules = rules.filter(rule => rule.enabled);
    if (enabledRules.length === 0) return { precision: 0, recall: 0, f1Score: 0 };

    const totalWeight = enabledRules.reduce((sum, rule) => sum + rule.weight, 0);
    
    const weightedPrecision = enabledRules.reduce((sum, rule) => 
      sum + (rule.currentPerformance.precision * rule.weight), 0) / totalWeight;
    
    const weightedRecall = enabledRules.reduce((sum, rule) => 
      sum + (rule.currentPerformance.recall * rule.weight), 0) / totalWeight;
    
    const f1Score = 2 * (weightedPrecision * weightedRecall) / (weightedPrecision + weightedRecall);
    
    return {
      precision: weightedPrecision,
      recall: weightedRecall,
      f1Score: isNaN(f1Score) ? 0 : f1Score
    };
  }, [rules]);

  // Handle rule parameter changes
  const handleRuleChange = useCallback((ruleId: string, field: 'threshold' | 'weight' | 'enabled', value: number | boolean) => {
    setRules(prevRules => {
      const newRules = prevRules.map(rule => {
        if (rule.ruleId === ruleId) {
          const updatedRule = { ...rule, [field]: value };
          
          // Simulate performance recalculation based on threshold changes
          if (field === 'threshold' && typeof value === 'number') {
            const thresholdDelta = value - rule.currentThreshold;
            const sensitivity = 0.1; // How much performance changes per threshold unit
            
            // Simulate trade-off: higher thresholds generally increase precision, decrease recall
            const precisionDelta = thresholdDelta * sensitivity * (rule.operator === '>' ? 1 : -1);
            const recallDelta = -thresholdDelta * sensitivity * (rule.operator === '>' ? 1 : -1);
            
            updatedRule.currentPerformance = {
              ...rule.currentPerformance,
              precision: Math.max(0.1, Math.min(0.95, rule.currentPerformance.precision + precisionDelta)),
              recall: Math.max(0.1, Math.min(0.95, rule.currentPerformance.recall + recallDelta))
            };
            
            updatedRule.currentPerformance.f1Score = 
              2 * (updatedRule.currentPerformance.precision * updatedRule.currentPerformance.recall) / 
              (updatedRule.currentPerformance.precision + updatedRule.currentPerformance.recall);
          }
          
          return updatedRule;
        }
        return rule;
      });
      
      // Add to performance history
      if (field === 'threshold') {
        const newHistory: PerformanceHistory = {
          timestamp: new Date().toISOString(),
          overallF1: overallPerformance.f1Score,
          overallPrecision: overallPerformance.precision,
          overallRecall: overallPerformance.recall,
          changedRule: ruleId,
          changedFrom: prevRules.find(r => r.ruleId === ruleId)?.[field === 'threshold' ? 'currentThreshold' : field] as number,
          changedTo: value as number
        };
        setPerformanceHistory(prev => [...prev.slice(-9), newHistory]);
      }
      
      return newRules;
    });
    
    if (onRuleChange && typeof value === 'number') {
      const rule = rules.find(r => r.ruleId === ruleId);
      if (rule) {
        onRuleChange(ruleId, 
          field === 'threshold' ? value : rule.currentThreshold,
          field === 'weight' ? value : rule.weight,
          field === 'enabled' ? Boolean(value) : rule.enabled
        );
      }
    }
  }, [rules, onRuleChange, overallPerformance]);

  // Load optimization preset
  const handleLoadPreset = useCallback((presetId: string) => {
    const preset = OPTIMIZATION_PRESETS.find(p => p.id === presetId);
    if (!preset) return;
    
    setRules(prevRules => 
      prevRules.map(rule => {
        const presetRule = preset.rules.find(pr => pr.ruleId === rule.ruleId);
        if (presetRule) {
          return {
            ...rule,
            currentThreshold: presetRule.threshold,
            weight: presetRule.weight,
            enabled: presetRule.enabled
          };
        }
        return rule;
      })
    );
    
    setSelectedPreset(presetId);
    onLoadPreset?.(presetId);
  }, [onLoadPreset]);

  // Auto-optimization using simple hill climbing
  const handleAutoOptimize = useCallback(() => {
    if (!autoOptimize) return;
    
    const target = optimizationTarget;
    const currentScore = target === 'precision' ? overallPerformance.precision :
                        target === 'recall' ? overallPerformance.recall :
                        overallPerformance.f1Score;
    
    // Try small adjustments to each rule and keep improvements
    setRules(prevRules => {
      const newRules = [...prevRules];
      let improved = false;
      
      for (let i = 0; i < newRules.length; i++) {
        if (!newRules[i].enabled) continue;
        
        const rule = newRules[i];
        const originalThreshold = rule.currentThreshold;
        
        // Try increasing threshold
        const increasedThreshold = Math.min(rule.maxThreshold, originalThreshold + rule.step);
        if (increasedThreshold !== originalThreshold) {
          // Simulate performance with increased threshold
          const testRule = { ...rule, currentThreshold: increasedThreshold };
          // Simple heuristic: higher thresholds usually improve precision
          const estimatedImprovement = target === 'precision' ? 0.01 : 
                                      target === 'recall' ? -0.01 : 0.005;
          
          if (estimatedImprovement > 0) {
            newRules[i] = { ...rule, currentThreshold: increasedThreshold };
            improved = true;
          }
        }
      }
      
      return improved ? newRules : prevRules;
    });
  }, [autoOptimize, optimizationTarget, overallPerformance]);

  // Auto-optimize effect
  useEffect(() => {
    if (autoOptimize) {
      const interval = setInterval(handleAutoOptimize, 2000);
      return () => clearInterval(interval);
    }
  }, [autoOptimize, handleAutoOptimize]);

  // Prepare performance chart data
  const chartData = performanceHistory.map((entry, index) => ({
    step: index + 1,
    f1Score: entry.overallF1 * 100,
    precision: entry.overallPrecision * 100,
    recall: entry.overallRecall * 100
  }));

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: '#0D1117' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: '1px solid #30363D' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography sx={{
            color: 'var(--palantir-blue-600)',
            fontFamily: 'monospace',
            fontSize: '14px',
            fontWeight: 'bold'
          }}>
            INTERACTIVE RULE TUNING
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Run Analysis with Current Settings">
              <IconButton 
                onClick={() => onRunAnalysis?.(rules)}
                disabled={isAnalyzing}
                sx={{ color: isAnalyzing ? 'var(--palantir-text-muted)' : 'var(--palantir-status-success)' }}
              >
                {isAnalyzing ? <SpeedIcon className="animate-spin" /> : <RunIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Reset to Defaults">
              <IconButton onClick={() => setRules(DEFAULT_TUNABLE_RULES)} sx={{ color: 'var(--palantir-text-muted)' }}>
                <RestoreIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Save Current Configuration">
              <IconButton onClick={() => onSavePreset?.({
                id: `custom_${Date.now()}`,
                name: 'Custom Configuration',
                description: 'User-defined configuration',
                target: 'balanced',
                rules: rules.map(rule => ({
                  ruleId: rule.ruleId,
                  threshold: rule.currentThreshold,
                  weight: rule.weight,
                  enabled: rule.enabled
                }))
              })} sx={{ color: 'var(--palantir-text-muted)' }}>
                <SaveIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        
        {/* Performance Summary Cards */}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={{ xs: 4 }}>
            <Card sx={{ backgroundColor: '#161B22', border: '1px solid #30363D' }}>
              <CardContent sx={{ p: 1.5, textAlign: 'center' }}>
                <Typography sx={{ color: '#00D2FF', fontSize: '10px' }}>PRECISION</Typography>
                <Typography sx={{ color: 'var(--palantir-text-primary)', fontSize: '18px', fontFamily: 'monospace', fontWeight: 'bold' }}>
                  {(overallPerformance.precision * 100).toFixed(1)}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 4 }}>
            <Card sx={{ backgroundColor: '#161B22', border: '1px solid #30363D' }}>
              <CardContent sx={{ p: 1.5, textAlign: 'center' }}>
                <Typography sx={{ color: 'var(--palantir-status-success)', fontSize: '10px' }}>RECALL</Typography>
                <Typography sx={{ color: 'var(--palantir-text-primary)', fontSize: '18px', fontFamily: 'monospace', fontWeight: 'bold' }}>
                  {(overallPerformance.recall * 100).toFixed(1)}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 4 }}>
            <Card sx={{ backgroundColor: '#161B22', border: '1px solid #30363D' }}>
              <CardContent sx={{ p: 1.5, textAlign: 'center' }}>
                <Typography sx={{ color: '#FFD93D', fontSize: '10px' }}>F1-SCORE</Typography>
                <Typography sx={{ color: 'var(--palantir-text-primary)', fontSize: '18px', fontFamily: 'monospace', fontWeight: 'bold' }}>
                  {(overallPerformance.f1Score * 100).toFixed(1)}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Quick Presets */}
      <Box sx={{ p: 2, borderBottom: '1px solid #30363D' }}>
        <Typography sx={{
          color: '#00D2FF',
          fontFamily: 'monospace',
          fontSize: '12px',
          mb: 1
        }}>
          OPTIMIZATION PRESETS
        </Typography>
        <Grid container spacing={1}>
          {OPTIMIZATION_PRESETS.map((preset) => (
            <Grid size={{ xs: 4 }} key={preset.id}>
              <Button
                variant={selectedPreset === preset.id ? "contained" : "outlined"}
                size="small"
                fullWidth
                onClick={() => handleLoadPreset(preset.id)}
                sx={{
                  fontSize: '10px',
                  fontFamily: 'monospace',
                  backgroundColor: selectedPreset === preset.id ? 'var(--palantir-blue-600)' : 'transparent',
                  borderColor: '#30363D',
                  color: selectedPreset === preset.id ? '#000' : 'var(--palantir-text-primary)',
                  '&:hover': {
                    backgroundColor: selectedPreset === preset.id ? 'var(--palantir-blue-700)' : 'rgba(255, 107, 53, 0.1)',
                    borderColor: 'var(--palantir-blue-600)'
                  }
                }}
              >
                {preset.name}
              </Button>
            </Grid>
          ))}
        </Grid>
        
        {/* Auto-optimization Controls */}
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={autoOptimize}
                onChange={(e) => setAutoOptimize(e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography sx={{ color: 'var(--palantir-text-primary)', fontSize: '11px', fontFamily: 'monospace' }}>
                Auto-Optimize
              </Typography>
            }
          />
          {autoOptimize && (
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={optimizationTarget}
                onChange={(e) => setOptimizationTarget(e.target.value as any)}
                sx={{
                  color: 'var(--palantir-text-primary)',
                  fontSize: '11px',
                  fontFamily: 'monospace',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#30363D'
                  }
                }}
              >
                <MenuItem value="precision">Maximize Precision</MenuItem>
                <MenuItem value="recall">Maximize Recall</MenuItem>
                <MenuItem value="f1_score">Maximize F1-Score</MenuItem>
              </Select>
            </FormControl>
          )}
        </Box>
      </Box>

      {/* Performance History Chart */}
      {chartData.length > 1 && (
        <Box sx={{ p: 2, borderBottom: '1px solid #30363D' }}>
          <Typography sx={{
            color: '#00D2FF',
            fontFamily: 'monospace',
            fontSize: '12px',
            mb: 1
          }}>
            PERFORMANCE HISTORY
          </Typography>
          <Paper sx={{ p: 2, backgroundColor: '#161B22', height: 150 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#30363D" />
                <XAxis dataKey="step" tick={{ fill: 'var(--palantir-text-muted)', fontSize: 10 }} />
                <YAxis domain={[0, 100]} tick={{ fill: 'var(--palantir-text-muted)', fontSize: 10 }} />
                <RechartsTooltip
                  contentStyle={{ backgroundColor: '#0D1117', border: '1px solid #30363D' }}
                  labelStyle={{ color: 'var(--palantir-text-primary)' }}
                />
                <Line type="monotone" dataKey="precision" stroke="#00D2FF" strokeWidth={2} />
                <Line type="monotone" dataKey="recall" stroke="var(--palantir-status-success)" strokeWidth={2} />
                <Line type="monotone" dataKey="f1Score" stroke="#FFD93D" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Box>
      )}

      {/* Rules Tuning Interface */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography sx={{
            color: '#00D2FF',
            fontFamily: 'monospace',
            fontSize: '12px'
          }}>
            RULE PARAMETERS
          </Typography>
          <Button
            onClick={() => setShowAdvanced(!showAdvanced)}
            size="small"
            sx={{ color: 'var(--palantir-text-muted)', fontSize: '10px' }}
          >
            {showAdvanced ? 'Basic' : 'Advanced'} View
          </Button>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {rules.map((rule) => (
            <Accordion
              key={rule.ruleId}
              sx={{
                backgroundColor: '#161B22',
                border: '1px solid #30363D',
                '&:before': { display: 'none' }
              }}
            >
              <AccordionSummary expandIcon={<ExpandIcon sx={{ color: 'var(--palantir-text-muted)' }} />}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mr: 2 }}>
                  <Box>
                    <Typography sx={{
                      color: rule.enabled ? 'var(--palantir-text-primary)' : 'var(--palantir-text-muted)',
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      {rule.ruleName}
                    </Typography>
                    <Typography sx={{
                      color: 'var(--palantir-text-muted)',
                      fontSize: '10px'
                    }}>
                      {rule.description}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip
                      label={rule.category}
                      size="small"
                      sx={{
                        backgroundColor: '#30363D',
                        color: 'var(--palantir-text-primary)',
                        fontSize: '9px'
                      }}
                    />
                    <Typography sx={{
                      color: '#FFD93D',
                      fontFamily: 'monospace',
                      fontSize: '11px',
                      fontWeight: 'bold'
                    }}>
                      F1: {(rule.currentPerformance.f1Score * 100).toFixed(0)}%
                    </Typography>
                    <Switch
                      checked={rule.enabled}
                      onChange={(e) => handleRuleChange(rule.ruleId, 'enabled', e.target.checked)}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  </Box>
                </Box>
              </AccordionSummary>
              
              <AccordionDetails>
                <Grid container spacing={3}>
                  {/* Threshold Control */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box>
                      <Typography sx={{
                        color: '#00D2FF',
                        fontFamily: 'monospace',
                        fontSize: '11px',
                        mb: 1
                      }}>
                        THRESHOLD: {rule.currentThreshold} {rule.unit || ''}
                      </Typography>
                      <Slider
                        value={rule.currentThreshold}
                        onChange={(_, value) => handleRuleChange(rule.ruleId, 'threshold', value as number)}
                        min={rule.minThreshold}
                        max={rule.maxThreshold}
                        step={rule.step}
                        disabled={!rule.enabled}
                        marks={[
                          { value: rule.minThreshold, label: `${rule.minThreshold}` },
                          { value: rule.maxThreshold, label: `${rule.maxThreshold}` }
                        ]}
                        sx={{
                          color: 'var(--palantir-blue-600)',
                          '& .MuiSlider-thumb': {
                            backgroundColor: 'var(--palantir-blue-600)',
                            '&:hover': {
                              backgroundColor: 'var(--palantir-blue-700)'
                            }
                          },
                          '& .MuiSlider-track': {
                            backgroundColor: 'var(--palantir-blue-600)'
                          },
                          '& .MuiSlider-rail': {
                            backgroundColor: '#30363D'
                          },
                          '& .MuiSlider-mark': {
                            backgroundColor: 'var(--palantir-text-muted)'
                          },
                          '& .MuiSlider-markLabel': {
                            color: 'var(--palantir-text-muted)',
                            fontSize: '9px'
                          }
                        }}
                      />
                    </Box>
                  </Grid>

                  {/* Weight Control */}
                  {showAdvanced && (
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box>
                        <Typography sx={{
                          color: '#00D2FF',
                          fontFamily: 'monospace',
                          fontSize: '11px',
                          mb: 1
                        }}>
                          WEIGHT: {rule.weight}
                        </Typography>
                        <Slider
                          value={rule.weight}
                          onChange={(_, value) => handleRuleChange(rule.ruleId, 'weight', value as number)}
                          min={5}
                          max={50}
                          step={5}
                          disabled={!rule.enabled}
                          marks={[
                            { value: 5, label: '5' },
                            { value: 25, label: '25' },
                            { value: 50, label: '50' }
                          ]}
                          sx={{
                            color: '#00D2FF',
                            '& .MuiSlider-thumb': {
                              backgroundColor: '#00D2FF'
                            },
                            '& .MuiSlider-track': {
                              backgroundColor: '#00D2FF'
                            },
                            '& .MuiSlider-rail': {
                              backgroundColor: '#30363D'
                            },
                            '& .MuiSlider-markLabel': {
                              color: 'var(--palantir-text-muted)',
                              fontSize: '9px'
                            }
                          }}
                        />
                      </Box>
                    </Grid>
                  )}

                  {/* Performance Metrics */}
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ display: 'flex', gap: 3 }}>
                      <Box>
                        <Typography sx={{ color: 'var(--palantir-text-muted)', fontSize: '10px' }}>Precision</Typography>
                        <LinearProgress
                          variant="determinate"
                          value={rule.currentPerformance.precision * 100}
                          sx={{
                            width: 100,
                            height: 6,
                            backgroundColor: '#30363D',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: '#00D2FF'
                            }
                          }}
                        />
                        <Typography sx={{ color: 'var(--palantir-text-primary)', fontSize: '10px', fontFamily: 'monospace' }}>
                          {(rule.currentPerformance.precision * 100).toFixed(0)}%
                        </Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ color: 'var(--palantir-text-muted)', fontSize: '10px' }}>Recall</Typography>
                        <LinearProgress
                          variant="determinate"
                          value={rule.currentPerformance.recall * 100}
                          sx={{
                            width: 100,
                            height: 6,
                            backgroundColor: '#30363D',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: 'var(--palantir-status-success)'
                            }
                          }}
                        />
                        <Typography sx={{ color: 'var(--palantir-text-primary)', fontSize: '10px', fontFamily: 'monospace' }}>
                          {(rule.currentPerformance.recall * 100).toFixed(0)}%
                        </Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ color: 'var(--palantir-text-muted)', fontSize: '10px' }}>F1-Score</Typography>
                        <LinearProgress
                          variant="determinate"
                          value={rule.currentPerformance.f1Score * 100}
                          sx={{
                            width: 100,
                            height: 6,
                            backgroundColor: '#30363D',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: '#FFD93D'
                            }
                          }}
                        />
                        <Typography sx={{ color: 'var(--palantir-text-primary)', fontSize: '10px', fontFamily: 'monospace' }}>
                          {(rule.currentPerformance.f1Score * 100).toFixed(0)}%
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Analysis Status */}
        {isAnalyzing && (
          <Alert severity="info" sx={{ mt: 2, backgroundColor: '#21262D', color: 'var(--palantir-text-primary)' }}>
            <Typography sx={{ fontSize: '11px', fontFamily: 'monospace' }}>
              Running analysis with updated parameters... This may take 30-60 seconds.
            </Typography>
            <LinearProgress sx={{ mt: 1, backgroundColor: '#30363D' }} />
          </Alert>
        )}

        {/* Tips */}
        <Paper sx={{ mt: 2, p: 2, backgroundColor: '#0D1117', border: '1px solid #30363D' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <InfoIcon sx={{ color: '#FFD93D', fontSize: 16 }} />
            <Typography sx={{
              color: '#FFD93D',
              fontFamily: 'monospace',
              fontSize: '11px',
              fontWeight: 'bold'
            }}>
              TUNING TIPS
            </Typography>
          </Box>
          <Typography sx={{
            color: 'var(--palantir-text-primary)',
            fontSize: '10px',
            lineHeight: 1.6,
            fontFamily: 'monospace'
          }}>
            • Higher thresholds typically increase precision but decrease recall
            <br />• Start with presets, then fine-tune individual rules
            <br />• Monitor F1-score for balanced performance
            <br />• Use auto-optimize for quick improvements
            <br />• Save configurations before major changes
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
};

export default InteractiveRuleTuningPanel;